import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const quantitySelector = /** @type {any} */ (form.querySelector('quantity-selector-component'));
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }

    this.animateAddToCart();

    const animationEnabled = this.dataset.addToCartAnimation === 'true';

    if (animationEnabled && !event.target.closest('.quick-add-modal')) {
      this.#animateFlyToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Fetches cart and updates quantity selector for current variant
   * @returns {Promise<number>} The cart quantity for the current variant
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return 0;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      const cartItem = cart.items.find(
        /** @param {any} item */
        (item) => item.variant_id.toString() === variantIdInput.value.toString()
      );
      const cartQty = cartItem ? cartItem.quantity : 0;

      // Use public API to update quantity selector
      const quantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
      if (quantitySelector?.setCartQuantity) {
        quantitySelector.setCartQuantity(cartQty);
      }

      // Update quantity label if it exists
      this.#updateQuantityLabel(cartQty);

      return cartQty;
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return 0;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;

    await this.#fetchAndUpdateCartQuantity();
  };

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError, addToCartButtonContainer } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Check if the add to cart button is disabled and do an early return if it is
    if (addToCartButtonContainer?.refs.addToCartButton?.disabled) return;

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

    const quantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        addToCartButtonContainer?.disable();

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity.toString());
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          addToCartButtonContainer?.enable();
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    // Use standard form submission for compatibility with third-party cart apps.
    this.#handleStandardCartSubmit(event, form);
  }

  /**
   * Handles standard cart submission (legacy flow for products without personalization)
   * @param {Event} event
   * @param {HTMLFormElement} form
   */
  #handleStandardCartSubmit(event, form) {
    const { addToCartTextError, addToCartButtonContainer } = this.refs;
    const formData = new FormData(form);
    const sectionScope = form.closest('.shopify-section, [id^="shopify-section-"]') || document;

    // Home puede reemplazar/rehidratar el formulario y perder hidden inputs dinámicos.
    // Refuerzo: inyectar explícitamente las properties críticas en el payload final.
    const baseProductTitle =
      (window.personalizationProductSettings?.base_product_title || '').toString().trim() ||
      (document.querySelector('h1')?.textContent || '').trim();
    const selectedVariantTitle =
      (window.personalizationProductSettings?.base_product_variant || '').toString().trim() ||
      (sectionScope.querySelector('.size-option.active')?.dataset?.size || '').toString().trim();
    const baseProductWithVariant =
      baseProductTitle && selectedVariantTitle
        ? `${baseProductTitle} / ${selectedVariantTitle}`
        : baseProductTitle;

    // Addon de personalización (items[1]): reconstrucción defensiva para Home.
    const activeCustomizationOption =
      sectionScope.querySelector('.customization-type-wrapper .customization-option.active') ||
      document.querySelector('.customization-type-wrapper .customization-option.active');
    const effectiveCustomizationType =
      (activeCustomizationOption?.dataset?.value || '').toString().trim() ||
      (document.querySelector('#customization_type')?.value || '').toString().trim() ||
      (formData.get('customization_type') || '').toString().trim();
    const activePersonalizationVariant =
      sectionScope.querySelector('.personalization-variant-option.active');
    const fallbackAddonVariantId =
      (activeCustomizationOption?.dataset?.playerVariantId || '').toString().trim() ||
      (activePersonalizationVariant?.dataset?.variantId || '').toString().trim() ||
      (document.querySelector('input[name="items[1]id"]')?.value || '').toString().trim() ||
      (window.selectedPersonalizationProductId || '').toString().trim() ||
      (formData.get('items[1]id') || '').toString().trim();
    const shouldAddPersonalizationAddon =
      (effectiveCustomizationType === 'player' || effectiveCustomizationType === 'user') &&
      Boolean(fallbackAddonVariantId);

    if (shouldAddPersonalizationAddon) {
      formData.set('items[1]id', fallbackAddonVariantId);
      formData.set('items[1]quantity', '1');
      const parentVariantId = (formData.get('id') || '').toString().trim();
      if (parentVariantId) {
        formData.set('items[1]parent_id', parentVariantId);
      }

      const nombreValue =
        (form.querySelector('#product_form_player_name')?.value || document.getElementById('product_form_player_name')?.value || '').toString().trim();
      const dorsalValue =
        (form.querySelector('#product_form_player_number')?.value || document.getElementById('product_form_player_number')?.value || '').toString().trim();
      const parcheValue =
        (form.querySelector('#product_form_team_patch')?.value || document.getElementById('product_form_team_patch')?.value || '').toString().trim();

      if (nombreValue) formData.set('items[1]properties[Nombre]', nombreValue);
      if (dorsalValue) formData.set('items[1]properties[Dorsal]', dorsalValue);
      if (parcheValue) formData.set('items[1]properties[Parche de equipo]', parcheValue);
      if (baseProductWithVariant) formData.set('items[1]properties[Producto]', baseProductWithVariant);
    }

    // Addon de parche (items[2]): reconstruir si hay parche seleccionado
    const patchAddonVariantIdFromInput =
      (form.querySelector('#patch_addon_variant_id')?.value || document.getElementById('patch_addon_variant_id')?.value || '').toString().trim();
    const selectedPatchVariantId = patchAddonVariantIdFromInput ||
      (window.selectedPatchData?.variantId ? String(window.selectedPatchData.variantId) : '');
    const selectedPatchType = (window.selectedPatchData?.type || '').toString().trim().toLowerCase();
    const patchLabel =
      (form.querySelector('#product_form_team_patch')?.value || document.getElementById('product_form_team_patch')?.value || '').toString().trim();
    const parentVariantId = (formData.get('id') || '').toString().trim();
    const shouldAddPatchAddon =
      Boolean(selectedPatchVariantId) &&
      patchLabel !== '';

    if (shouldAddPatchAddon) {
      formData.set('items[2]id', selectedPatchVariantId);
      formData.set('items[2]quantity', '1');
      if (parentVariantId) {
        formData.set('items[2]parent_id', parentVariantId);
      }
      if (patchLabel) {
        formData.set('items[2]properties[Parche de equipo]', patchLabel);
      }
      if (baseProductWithVariant) {
        formData.set('items[2]properties[Producto]', baseProductWithVariant);
      }
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
            const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
            const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          // Fetch the updated cart to get the actual total quantity for this variant
          this.#fetchAndUpdateCartQuantity();

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');

    // Update the variant ID
    variantId.value = event.detail.resource?.id ?? '';

    if (!currentAddToCartButton && !this.refs.acceleratedCheckoutButtonContainer) return;

    // Update the button state
    if (currentAddToCartButton) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        addToCartButtonContainer.disable();
      } else {
        addToCartButtonContainer.enable();
      }

      // Update the add to cart button text and icon
      if (newAddToCartButton) {
        morph(currentAddToCartButton, newAddToCartButton);
      }
    }

    if (this.refs.acceleratedCheckoutButtonContainer) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const quantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(
        newQuantityInput.min,
        newQuantityInput.max || null,
        newQuantityInput.step
      );
    }

    // Check if quantity rules are appearing/disappearing (causes layout shift)
    const quantityRules = this.refs.quantityRules;
    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    if (isQuantityRulesChanging && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const currentProductFormButtons = this.refs.productFormButtons;
      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

      if (currentProductFormButtons && newProductFormButtons) {
        morph(currentProductFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (newQuantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          newQuantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          newQuantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      const quantityLabel = this.querySelector('.quantity-label');
      const newQuantityLabel = event.detail.data.html.querySelector('.quantity-label');
      this.#morphOrUpdateElement(quantityLabel, newQuantityLabel, quantitySelector);

      const addToCartButton = this.querySelector('[ref="addToCartButtonContainer"]');
      this.#morphOrUpdateElement(quantityRules, newQuantityRules, addToCartButton);
    }

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = '1';

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      //Update the position of the flying thingy
      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}

// ── Guardia de personalización ────────────────────────────────────────────────
// Se registra en fase de captura sobre `window`, que es lo primero que recorre un
// evento. Así el bloqueo ocurre antes de que lo vea ningún otro listener (tema o
// app de terceros), sea cual sea la vía por la que se acabe llamando al carrito.
if (!window.__vcfPersonalizationGuard) {
  window.__vcfPersonalizationGuard = true;

  // La tienda publica es / ca / en. Se elige por el idioma de la página.
  const TEXTOS = {
    es: {
      ambos: 'Escribe el nombre y el dorsal para personalizar tu camiseta.',
      nombre: 'Escribe el nombre para personalizar tu camiseta.',
      dorsal: 'Escribe el dorsal para personalizar tu camiseta.',
    },
    ca: {
      ambos: 'Escriu el nom i el dorsal per a personalitzar la teua samarreta.',
      nombre: 'Escriu el nom per a personalitzar la teua samarreta.',
      dorsal: 'Escriu el dorsal per a personalitzar la teua samarreta.',
    },
    en: {
      ambos: 'Enter a name and a number to personalise your shirt.',
      nombre: 'Enter a name to personalise your shirt.',
      dorsal: 'Enter a number to personalise your shirt.',
    },
  };

  const idioma = (document.documentElement.lang || 'es').slice(0, 2).toLowerCase();
  const MENSAJES = TEXTOS[idioma] || TEXTOS.es;

  // Prefiere el elemento que el cliente está viendo: la ficha puede tener más de una
  // instancia del personalizador (PDP y customizador de portada) con los mismos ids.
  const valorDe = (selector) => {
    const encontrados = [...document.querySelectorAll(selector)];
    const visible = encontrados.find((el) => el.offsetParent !== null);
    return ((visible || encontrados[0])?.value || '').toString().trim();
  };

  /**
   * Devuelve el mensaje de error si falta algún dato de personalización, o null si todo está bien.
   * @returns {string | null}
   */
  const queFalta = () => {
    // Sin personalización activa no hay nada que validar.
    const tipoActivo = (
      document.querySelector('.customization-type-wrapper .customization-option.active')?.dataset?.value ||
      document.querySelector('#customization_type')?.value ||
      ''
    ).toString().trim();
    const addonActivo = [...document.querySelectorAll('input[name="items[1]id"]')].some(
      (input) => !input.disabled && input.value
    );
    if (tipoActivo !== 'player' && tipoActivo !== 'user' && !addonActivo) return null;

    let modo;
    let nombre;
    let dorsal;

    if (tipoActivo === 'player') {
      // Con jugador de plantilla el nombre y el dorsal salen de la ficha, no de los campos.
      modo = 'name-number';
      nombre = valorDe('#addon_property_nombre') || valorDe('#product_form_player_name');
      dorsal = valorDe('#addon_property_dorsal') || valorDe('#product_form_player_number');
    } else {
      // Personalización libre: manda lo que el cliente ve escrito, sin recurrir a los
      // campos espejo, que pueden conservar el valor de una selección anterior.
      modo = valorDe('#personalization_variant') || 'name-number';
      nombre = valorDe('#customization_user_name');
      dorsal = valorDe('#customization_user_number');
    }

    const faltaNombre = modo !== 'number-only' && !nombre;
    const faltaDorsal = modo !== 'name-only' && !dorsal;

    if (faltaNombre && faltaDorsal) return MENSAJES.ambos;
    if (faltaNombre) return MENSAJES.nombre;
    if (faltaDorsal) return MENSAJES.dorsal;
    return null;
  };

  const avisar = (mensaje, scope) => {
    const contenedor =
      (scope || document).querySelector('[ref="addToCartTextError"]') ||
      document.querySelector('[ref="addToCartTextError"]');

    if (contenedor) {
      contenedor.classList.remove('hidden');
      const nodoTexto = contenedor.childNodes[2];
      if (nodoTexto) nodoTexto.textContent = mensaje;
      else contenedor.appendChild(document.createTextNode(mensaje));
      setTimeout(() => contenedor.classList.add('hidden'), ERROR_MESSAGE_DISPLAY_DURATION);
    } else {
      alert(mensaje);
    }

    document.querySelector('.add-to-cart-button.atc-added')?.classList.remove('atc-added');

    const campo = mensaje === MENSAJES.dorsal
      ? document.querySelector('#customization_user_number')
      : document.querySelector('#customization_user_name');
    if (campo instanceof HTMLElement && campo.offsetParent !== null) campo.focus();
  };

  const bloquear = (event, mensaje, scope) => {
    event.preventDefault();
    event.stopImmediatePropagation();
    avisar(mensaje, scope);
  };

  window.addEventListener(
    'click',
    (event) => {
      const boton = event.target?.closest?.('button[name="add"], .add-to-cart-button, [data-add-to-cart]');
      if (!boton || boton.disabled) return;
      const mensaje = queFalta();
      if (mensaje) bloquear(event, mensaje, boton.closest('product-form-component') || undefined);
    },
    true
  );

  window.addEventListener(
    'submit',
    (event) => {
      const formulario = event.target;
      const action = formulario?.getAttribute?.('action') || '';
      if (!action.includes('/cart/add')) return;
      const mensaje = queFalta();
      if (mensaje) bloquear(event, mensaje, formulario.closest('product-form-component') || undefined);
    },
    true
  );
}